import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app-language';
  private readonly DEFAULT_LANG = 'fr';
  private readonly DRIVER_DEFAULT_LANG = 'ar';
  private readonly translate = inject(TranslateService);

  readonly availableLanguages: Language[] = [
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', dir: 'ltr' },
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dir: 'ltr' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  ];

  currentLanguage = signal<Language>(this.availableLanguages[0]);

  constructor() {
    this.translate.addLangs(['ar', 'fr', 'en']);
    this.initLanguage();
  }

  private initLanguage(): void {
    const savedLang = localStorage.getItem(this.STORAGE_KEY);
    const userRole = this.getUserRole();
    const lastUserRole = localStorage.getItem('last-user-role');

    let langToUse = this.DEFAULT_LANG;

    if (savedLang && this.isLanguageAvailable(savedLang)) {
      langToUse = savedLang;
    } else if (userRole === 'DRIVER' || (!userRole && lastUserRole === 'DRIVER')) {
      langToUse = this.DRIVER_DEFAULT_LANG;
    } else {
      const browserLang = this.translate.getBrowserLang();
      if (browserLang && this.isLanguageAvailable(browserLang)) {
        langToUse = browserLang;
      }
    }

    this.setLanguage(langToUse);
  }

  private getUserRole(): string | null {
    try {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const user = JSON.parse(userJson);
        return user?.role || null;
      }
    } catch (error) {
      console.error('❌ Error parsing user from localStorage:', error);
    }
    return null;
  }

  private isLanguageAvailable(langCode: string | undefined): boolean {
    if (!langCode) return false;
    return this.availableLanguages.some((lang) => lang.code === langCode);
  }

  setLanguage(langCode: string): void {
    const language = this.availableLanguages.find((l) => l.code === langCode);

    if (!language) {
      console.warn(`⚠️ Langue ${langCode} non trouvée. Utilisation de ${this.DEFAULT_LANG}`);
      langCode = this.DEFAULT_LANG;
    }

    this.translate.use(langCode).subscribe({
      next: () => {
        const selectedLang = this.availableLanguages.find((l) => l.code === langCode)!;
        this.currentLanguage.set(selectedLang);
        localStorage.setItem(this.STORAGE_KEY, langCode);
        this.applyTextDirection(selectedLang.dir);
        document.documentElement.lang = langCode;
        console.log(`✅ Langue chargée: ${selectedLang.nativeName} (${langCode})`);
      },
      error: (err) => {
        console.error(`❌ Erreur chargement langue ${langCode}:`, err);
        console.error('Vérifiez que le fichier existe: ./assets/i18n/' + langCode + '.json');
      },
    });
  }

  private applyTextDirection(direction: 'ltr' | 'rtl'): void {
    document.documentElement.dir = direction;
    document.body.dir = direction;
    document.body.classList.toggle('rtl', direction === 'rtl');
    document.body.classList.toggle('ltr', direction === 'ltr');
  }

  getCurrentLanguage(): Language {
    return this.currentLanguage();
  }

  isRTL(): boolean {
    return this.currentLanguage().dir === 'rtl';
  }

  getCurrentLanguageCode(): string {
    return this.currentLanguage().code;
  }

  instant(key: string, params?: object): string {
    return this.translate.instant(key, params);
  }

  get(key: string, params?: object) {
    return this.translate.get(key, params);
  }

  reinitializeLanguageForUser(): void {
    const userRole = this.getUserRole();
    console.log('🔄 Reinitializing language for role:', userRole);

    if (userRole === 'DRIVER') {
      const savedLang = localStorage.getItem(this.STORAGE_KEY);
      if (!savedLang) {
        console.log('🚗 Setting Arabic for driver (no saved preference)');
        this.setLanguage(this.DRIVER_DEFAULT_LANG);
      }
    }
  }
}
